import { isDevMode, Component, OnInit, OnDestroy } from '@angular/core';
import { PhoneService } from '../../../services/phone/phone.service';
import { AuthenticationService } from '@wephone-core/service/authentication';
import { ElectronService } from 'ngx-electron';
import { TranslationService, TranslationLocale, _tk, _ti } from '@wephone-translation';
import {
  DialogService,
  IFlexDialogConfig,
  Platform,
  MessageService,
} from '@wephone-utils';
import { AutoUpdateService } from 'src/app/wephone-common/service/autoupdate.service';
import { MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormControl } from '@angular/forms';
import { DialogComponentBase } from '@wephone-core-ui';
import { TestHeadsetModalComponent } from '@wephone-app-phone/modals/test-headset-modal/test-headset-modal.component';
import { StorageService } from '@wephone-core/service/storage.service';
import { ConfigExecScript } from '@wephone-app-phone/services';
import * as _ from 'lodash';
import { UserEntity } from '@wephone-core/model/entity/user';
import { UserParamDict } from '@wephone-core/system';
import { ConfigManager } from '@wephone-core/wephone-core.module';
import { UserService } from '@wephone/services/user.service';
import { AudioDeviceService } from '@wephone-app-phone/services/audio-devices.service';

@Component({
  selector: 'user-common-settings',
  templateUrl: 'user-common-settings.html',
  styleUrls: ['./user-common-settings.scss']
})
export class UserCommonSettingsComponent extends DialogComponentBase implements OnInit, OnDestroy {
  static modalConfig: IFlexDialogConfig = {
    size: 'l',
    width: '500px',
    minHeight: '90%'
  };

  SCROLLABLE_CONTENT = true;

  dialogTitle = _tk('menu.settings');

  audioInputName = new FormControl();
  audioOutputName = new FormControl();
  ringDeviceName = new FormControl();

  sipstackEnabled: boolean;
  devices: any[] = [];
  openBoOnCall: boolean;

  audioInputOptions: any[] = [];
  audioOutputOptions: any[] = [];
  ringDeviceOptions: any[] = [];
  inputVolume: number;
  outputVolume: number;
  ringVolume: number;

  showSelect = false; // used to fix translate bug in ion-option
  appLanguage: FormControl;
  languages: TranslationLocale[];
  boIframePositions = [
    { value: 0, label: 'components.settings.iframe_position_right' },
    { value: 1, label: 'components.settings.iframe_position_left' },
  ];

  // For account-basic-settings
  currentUser: UserEntity;
  devMode: boolean;
  form: FormGroup;
  appAutoLaunch: any;
  autoLaunchEnable: boolean;
  autoLaunchAvailable: boolean;
  appVersion = '';
  versionSHA = '';
  buildDate = '';
  vocalCommandUrl = '';
  userParams: UserParamDict = {
    open_bo_on_call: 0,
    iframe_bo_position: 0,
    ringtone_id: undefined,
    mfa_enabled: 0
  };

  // Hotkey
  hotkeyConfig: {
    status?: boolean;
    meta_key?: string;
    event_key?: string;
    instant_dial?: boolean;
  } = {};

  execScriptConfig: ConfigExecScript = {};

  isOnMacOS = false;
  isElectron = false;

  openBoOnCallCtrl: FormControl;
  iframePositionCtrl: FormControl;

  constructor(
    dialogRef: MatDialogRef<UserCommonSettingsComponent>,
    public autoUpdate: AutoUpdateService,
    public phoneService: PhoneService,
    public translation: TranslationService,
    private readonly electronService: ElectronService,
    private readonly storage: StorageService,
    public dialogService: DialogService,
    private readonly platform: Platform,
    private readonly messageService: MessageService,
    public authService: AuthenticationService,
    private readonly configService: ConfigManager,
    private readonly userService: UserService,
    private readonly audioDeviceService: AudioDeviceService,
  ) {
    super(dialogRef);
    if (this.electronService.process && this.electronService.process.platform) {
      this.isOnMacOS = this.electronService.process.platform === 'darwin';
    }

    this.devMode = isDevMode();
    this.isElectron = this.platform.isElectronApp();
    this.appVersion = window['APPLICATION_VERSION'];
    this.versionSHA = window['VERSION_SHA'];
    this.buildDate = window['BUILD_DATE'];
    this.currentUser = authService.getUser();

    this.languages = this.translation.availableLangs; // .map((x) => x.label)
    this.initAutoLaunchAvailable();
    if (this.autoLaunchAvailable) {
      const AutoLaunch = window['EREMOTE'].require('auto-launch');
      this.appAutoLaunch = new AutoLaunch({ name: 'Graam' });
      this.autoLaunchEnable = false;
      this.appAutoLaunch.isEnabled().then(isEnabled => {
        this.autoLaunchEnable = isEnabled ? true : false;
        this.updateDialogLayout();
      });
    }
    this.storage.getAppConfig('vocal_command_url').then((url: string) => {
      this.vocalCommandUrl = url;
    });

    this.hotkeyConfig = {
      status: true,
      meta_key: 'control',
      event_key: 'D',
      instant_dial: false
    };
    this.storage.getAppConfig('hotkey_config').then((hotkeyConfigData: any) => {
      _.extend(this.hotkeyConfig, hotkeyConfigData);
      this.updateDialogLayout();
    });

    this.phoneService.getConfigExecScript().then(
      (execScriptData: ConfigExecScript) => {
        this.execScriptConfig = execScriptData;
      }
    );

    const userParams = this.configService.getUserParams();
    _.extend(this.userParams, userParams);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.appLanguage = new FormControl(this.translation.getCurrLanguage().code);
    this.appLanguage.valueChanges.subscribe(lang => {
      this.changeLanguage(lang);
    });
    this.audioInputOptions = [];
    this.audioOutputOptions = [];
    this.ringDeviceOptions = [];
    this.devices = this.phoneService.getAudioDevices();
    for (const device of this.devices) {
      if (device['is_output'] === 1) {
        this.audioOutputOptions.push(device);
        this.ringDeviceOptions.push(device);
      }
      if (device['is_input'] === 1) {
        this.audioInputOptions.push(device);
      }
    }

    this.showSelect = true;
    this.phoneService.loadPhoneConfig().then(phone_config => {
      this.sipstackEnabled = phone_config.sipstack_enabled;
      this.inputVolume = +phone_config.input_volume;
      this.outputVolume = +phone_config.output_volume;
      this.ringVolume = +phone_config.ring_volume;
      this.audioInputName.setValue(this.phoneService.getActiveCaptureDeviceName());
      this.audioOutputName.setValue(this.phoneService.getActivePlaybackDeviceName());
      this.ringDeviceName.setValue(this.phoneService.getActiveRingDeviceName());

      this.audioInputName.valueChanges.subscribe(input => {
        this.changeAudioInput(input);
      });

      this.audioOutputName.valueChanges.subscribe(output => {
        this.changeAudioOutput(output);
      });

      this.ringDeviceName.valueChanges.subscribe(output => {
        this.changeRingDevice(output);
      });
    });

    this.addSubscription(
      this.messageService.subscribe(
        'audio_device.change',
        e => {
          this.audioInputName.setValue(this.phoneService.getActiveCaptureDeviceName());
          this.audioOutputName.setValue(this.phoneService.getActivePlaybackDeviceName());
          this.ringDeviceName.setValue(this.phoneService.getActiveRingDeviceName());
        }
      )
    );

    this.openBoOnCallCtrl = new FormControl(this.userParams.open_bo_on_call);
    this.iframePositionCtrl = new FormControl(this.userParams.iframe_bo_position);
    this.openBoOnCallCtrl.valueChanges.subscribe(() => {
      this.changeOpenBoOnCall();
    });
    this.iframePositionCtrl.valueChanges.subscribe(() => {
      this.changeOpenBoOnCall();
    });
  }

  updateLayout(): void {
    this.updateDialogLayout();
  }

  private initAutoLaunchAvailable(): void {
    this.autoLaunchAvailable = false;

    if (!this.electronService.process || !this.electronService.process.platform) {
      return;
    }
    const platform: string = this.electronService.process.platform;

    if (/^win/.test(platform)) {
      this.autoLaunchAvailable = true;
      return;
    }
    if (/darwin/.test(platform)) {
      this.autoLaunchAvailable = true;
      return;
    }
    if (/linux/.test(platform)) {
      this.autoLaunchAvailable = true;
      return;
    }
  }

  changeAudioInput(inputDevice: string): void {
    this.phoneService.applyPhoneConfig({ input_device: inputDevice });
  }

  changeAudioOutput(outputDevice: string): void {
    this.phoneService.applyPhoneConfig({ output_device: outputDevice });
  }

  changeRingDevice(inputDevice: string): void {
    this.phoneService.applyPhoneConfig({ ring_device: inputDevice });
  }

  changeLanguage(language: string): void {
    this.translation.setAppLanguage(language);
  }

  onChangeInputVolume(event): void {
    this.phoneService.applyPhoneConfig({ input_volume: event.value });
  }

  onChangeOutputVolume(event): void {
    this.phoneService.applyPhoneConfig({ output_volume: event.value });
  }

  onChangeRingVolume(event): void {
    this.phoneService.applyPhoneConfig({ ring_volume: event.value });
  }

  async setSipstackMode(): Promise<void> {
    this.phoneService.applyPhoneConfig({ sipstack_enabled: this.sipstackEnabled });
    if (this.sipstackEnabled) {
      await this.phoneService.loadJsPhonelib();
      this.phoneService.configureAccounts();
    } else {
      this.phoneService.disable();
    }
  }

  setVocalCommandUrl(): void {
    this.storage.setAppConfig('vocal_command_url', this.vocalCommandUrl);
  }

  changeAutoLaunchState(): void {
    if (this.autoLaunchAvailable) {
      if (this.autoLaunchEnable) {
        this.appAutoLaunch.enable().then(() => {
          this.successToast(_ti('public.message.update_success'));
        });
      } else {
        this.appAutoLaunch.disable().then(() => {
          this.successToast(_ti('public.message.update_success'));
        });
      }
    }
  }

  downloadUpdate(): void {
    this.autoUpdate.startDownloading();
    this.modalRef.returnValue(undefined);
  }

  goWizardHeadset(): MatDialogRef<any, any> {
    return this.dialogService.openDialog2(TestHeadsetModalComponent, {});
  }

  setHotkeyConfigEventKey(event: any): void {
    event.preventDefault();
    // console.log('keydown event', event);

    if (event.keyCode === 17) {
      return;
    }

    const keyCode: number = event.keyCode >= 96 && event.keyCode <= 105 ? event.keyCode - 48 : event.keyCode;
    let eventKey: any;

    switch (keyCode) {
      case 37:
        eventKey = 'left';
        break;
      case 38:
        eventKey = 'up';
        break;
      case 39:
        eventKey = 'right';
        break;
      case 40:
        eventKey = 'down';
        break;
      case 32:
        eventKey = 'space';
        break;
      case 19: // Pause
      case 106: // * - Multiply
      case 107: // Add
      case 144: // NumLock
      case 145: // ScrollLock
        break;
      default:
        eventKey = event.key;
        break;
    }

    if (!eventKey) {
      this.showError('Cannot use this key into hotkey');
      return;
    }
    this.hotkeyConfig.event_key = eventKey;
    this.updateHotkeyConfig();
  }

  updateHotkeyConfig(): void {
    this.storage.setAppConfig('hotkey_config', this.hotkeyConfig);
  }

  updateExecScriptConfig(): void {
    this.phoneService.setConfigExecScript(this.execScriptConfig);
  }

  getSymbol(key: string): string {
    if (!key) {
      return '';
    }
    const map: any = {
      command: '\u2318', // ⌘
      shift: '\u21E7', // ⇧
      left: '\u2190', // ←
      right: '\u2192', // →
      up: '\u2191', // ↑
      down: '\u2193', // ↓
      enter: '\u23CE', // ⏎
      backspace: '\u232B' // ⌫
    };

    return map[key.toLowerCase()] || key;
  }

  async changeOpenBoOnCall(): Promise<void> {
    const data = {
      open_bo_on_call: this.openBoOnCallCtrl.value ? 1 : 0,
      iframe_bo_position: this.iframePositionCtrl.value,
    };
    await this.userService.updateUserParameters(data);
    this.configService.setUserParams(data);
  }

  changeAudioDeviceSettings(): void {
    this.audioDeviceService.openAudioDeviceSettings();
  }
}
